import { createTheme, Theme } from "@mui/material/styles";
import { aamericaTheme } from "./aamerica";
import { flexsteelTheme } from "./flexsteel";
import { homestylesTheme } from "./homestyles";
import { nestingaleTheme } from "./nestingale";
import { gtrTheme } from "./gtr";

const defaultThemeName = 'nestingaleTheme';

const themeMap: { [key: string]: Theme } = {
    nestingaleTheme,
    aamericaTheme,
    homestylesTheme,
    flexsteelTheme,
    gtrTheme
}

export function themeCreator(theme: string): Theme {
    if (themeMap[theme]) {
        return themeMap[theme];
    } else {
        return themeMap[defaultThemeName];
    }
}

export function defaultTheme() {
    const paletteColors = {
        primary: {
            light: '#FFFFFF',
            main: '#0D908E',
            dark: '#25282B',
            contrastText: '#fff',            
        },
        secondary: {
            main: '#A0A4AB',
            dark: '#52575C',
            light: '#A0A4A8'
        },
        primary_extent: {
            mainDark: "#076665"
        },
        additional: {
            color_01: '#FFFFFF'
        },
        btn_colors: {
            color_01: '#003E58'
        },
        buttons_bg: {
            primary: '#FE6526',
            primary_hover: '#F98F45',
            secondary: '#FFFFFF',
            info: '#FFFFFF',
            primary_dark: '#f55817'            
        },
        buttons_text: {
            primary: '#FFFFFF',
            secondary: '#FE6526',
            secondary_hover: '#F98F45',
            info: '#095F6A',
            info_hover: '#138c9c'  
        },
        backgrounds: {
            bg_1: '#003E58',
            bg_2: '#DFF1F0',
            bg_3: '#FEF4EC',
            bg_4: '#095F6A',
        }
    }

    // Create base theme first to get access to breakpoints
    const baseTheme = createTheme({
        breakpoints: {
            values: {
                xs: 0,
                sm: 560,
                md: 767,
                lg: 1200,
                xl: 1536,
            }
        }
    });

    return createTheme({
        breakpoints: baseTheme.breakpoints,
        palette: paletteColors,
        typography: {
            fontFamily: ['Nunito', 'Lora'].join(','),
            h1: {
                fontSize: '24px',
                color: paletteColors.secondary.dark,
                lineHeight: '32px',
                letterSpacing: '0.2px',
                fontWeight: 600,
                [baseTheme.breakpoints.down('sm')]: {
                    fontSize: '20px',
                    lineHeight: '26px'
                },
            },
            h2: {
                fontSize: '16px',
                color: paletteColors.primary.dark,
                lineHeight: '20px',
                letterSpacing: '0.2px',
                fontWeight: 600
            },
            h3: {
                fontSize: '16px',
                color: paletteColors.primary.dark,
                lineHeight: '20px',
                letterSpacing: '0.2px',
                fontWeight: 600,
                textTransform: 'uppercase'
            },
            body2: {
                color: paletteColors.primary.dark,
                fontSize: '14px',
                lineHeight: '22px',
                letterSpacing: '0.1px'
            },
            subtitle1: {
                color: paletteColors.primary.dark,
                fontSize: '16px',
                lineHeight: '24px',
                letterSpacing: '0.1px',
                fontWeight: 500
            },
            subtitle2: {
                color: paletteColors.primary.dark,
                fontSize: '14px',
                lineHeight: '22px',
                letterSpacing: '0.1px',
                textTransform: 'capitalize'
            },
            h5: {
                fontSize: '16px',
                color: paletteColors.primary.dark,
                lineHeight: '24px',
                letterSpacing: '0.2px',
                fontWeight: 600
            },
            h6: {
                fontSize: '14px',
                color: paletteColors.primary.dark,
                lineHeight: '22px',
                letterSpacing: '0.2px',
                fontWeight: 600
            }
        },
        components: {
            MuiTab: {
                defaultProps: {
                    disableRipple: true,
                },
                styleOverrides: {
                    root: {
                        margin: '0 20px',
                        paddingLeft: 0,
                        paddingRight: 0,
                        minWidth: 'initial'
                    }
                }
            },
            MuiDivider: {
                styleOverrides: {
                    root: {
                        borderColor: paletteColors.secondary.light
                    }
                }
            },
            MuiBreadcrumbs: {
                styleOverrides: {
                    root: ({ theme }: any) => ({
                        fontSize: '14px',
                        color: theme.palette.primary?.dark || '#25282B',
                        li: {
                            margin: '0px'
                        },
                        a: {
                            color: theme.palette.primary?.dark || '#25282B',
                            textDecoration: 'none',
                            '&:hover': {
                                color: theme.palette.primary?.main || '#0D908E'
                            }
                        }
                    })
                }
            },
            MuiRating: {
                styleOverrides: {
                    root: {
                        color: paletteColors.primary.main,
                        fontSize: '20px'
                    }
                }
            },
            MuiButtonBase: {
                defaultProps: {
                    // The props to change the default for.
                    disableRipple: true, // No more ripple, on the whole application 💣!
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: ({ ownerState, theme }: any) => {
                        const p = theme.palette;
                        const isContained = ownerState.variant === 'contained';
                        return {
                            // ── Shared base defaults ──
                            borderRadius: '35px',
                            boxShadow: 'none',
                            fontStyle: 'normal',
                            borderStyle: 'solid',
                            fontSize: '16px',
                            padding: '5px 30px',
                            borderWidth: '2px',

                            // ── Contained variant: full colored backgrounds (CTA buttons) ──
                            ...(isContained && ownerState.color === 'primary' && {
                                backgroundColor: p.buttons_bg?.primary,
                                color: p.buttons_text?.primary,
                                borderColor: p.buttons_text?.primary,
                                textTransform: 'uppercase',
                                '&:hover': {
                                    backgroundColor: p.buttons_bg?.primary_hover,
                                    color: p.buttons_text?.primary,
                                }
                            }),
                            ...(isContained && ownerState.color === 'secondary' && {
                                backgroundColor: p.buttons_bg?.secondary,
                                color: p.buttons_text?.secondary,
                                borderColor: p.buttons_text?.secondary,
                                textTransform: 'uppercase',
                                '&:hover': {
                                    backgroundColor: p.buttons_bg?.secondary,
                                    color: p.buttons_text?.secondary_hover,
                                    borderColor: p.buttons_text?.secondary_hover,
                                }
                            }),
                            ...(isContained && ownerState.color === 'info' && {
                                backgroundColor: p.buttons_bg?.info,
                                color: p.buttons_text?.info,
                                borderColor: p.buttons_text?.info,
                                textTransform: 'uppercase',
                                '&:hover': {
                                    backgroundColor: p.buttons_bg?.info,
                                    color: p.buttons_text?.info_hover,
                                    borderColor: p.buttons_text?.info_hover,
                                }
                            }),
                            ...(isContained && ownerState.color === 'success' && {
                                backgroundColor: p.primary?.main,
                                color: p.buttons_text?.primary,
                                borderColor: p.buttons_text?.primary,
                                '&:hover': {
                                    backgroundColor: p.primary?.main,
                                    color: p.buttons_text?.primary,
                                }
                            }),

                            // ── Text / Outlined: neutral style (dropdowns, filters) ──
                            ...(!isContained && {
                                backgroundColor: 'transparent',
                                color: p.primary?.dark || '#25282B',
                                borderColor: 'transparent',
                                borderWidth: '0px',
                                textTransform: 'uppercase',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    boxShadow: 'none',
                                }
                            }),
                        };
                    }
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        fontSize: '12px',
                        '.MuiOutlinedInput-input': {
                            padding: '10px 14px'
                        }
                    }
                }
            },
            MuiFormLabel: {
                styleOverrides: {
                    root: {
                        fontSize: '16px',
                        fontWeight: '500'
                    }
                }
            },
            MuiLinearProgress: {
                styleOverrides: {
                    barColorPrimary: {
                        backgroundColor: paletteColors.primary.main
                    },
                    colorPrimary: {
                        backgroundColor: paletteColors.secondary.light
                    },
                    root: {
                        height: '2px'
                    }
                }
            },
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        color: paletteColors.primary.dark,
                        fontSize: '15px',
                        'p, div': {
                            fontSize: '15px',
                            color: paletteColors.primary.dark
                        }
                    }
                }
            }
        }
    });
}


